import { survivorTagOverrides } from './survivorTagOverrides'
import { getSurvivorGameplayPerkId } from './survivorPerkEquivalents'
import { getSurvivorInteractionRoleIds } from './survivorPerkInteractions'

const ROLE_BY_TAG = {
  aura: 'aura_information',
  chase: 'chase',
  pallet: 'pallet_play',
  disparition: 'disappearance',
  stealth: 'stealth',
  heal: 'heal_reset',
  boon: 'boon_setup',
  totem: 'totem_interaction',
  item: 'item_economy',
  gen: 'generator_macro',
  skillcheck: 'skillcheck_value',
  anti_tunnel: 'anti_tunnel',
  anti_slug: 'anti_slug',
  pickup_denial: 'pickup_denial',
  endgame: 'endgame',
  basement: 'basement',
  teamplay: 'teamplay',
  save: 'save',
  obsession: 'obsession'
}

const ROLE_OVERRIDES = {
  a_place_for_us: {
    primaryRole: 'post_heal_stealth',
    secondaryRoles: ['heal_reset', 'teammate_locator', 'line_of_sight_break'],
    optionalRoles: ['post_heal_link']
  },
  ace_in_the_hole: {
    primaryRole: 'chest_loot',
    secondaryRoles: ['item_quality'],
    optionalRoles: []
  },
  aftercare: {
    primaryRole: 'post_heal_link',
    secondaryRoles: ['teammate_locator'],
    optionalRoles: []
  },
  appraisal: {
    primaryRole: 'chest_loot',
    secondaryRoles: ['item_conversion'],
    optionalRoles: []
  },
  bite_the_bullet: {
    primaryRole: 'heal_noise_denial',
    secondaryRoles: ['heal_reset', 'noise_denial'],
    optionalRoles: ['line_of_sight_break']
  },
  calm_spirit: {
    primaryRole: 'stealth_noise_denial',
    secondaryRoles: ['totem_interaction'],
    optionalRoles: []
  },
  balanced_landing: {
    primaryRole: 'vertical_exhaustion_chase',
    secondaryRoles: ['chase_reposition', 'map_routing'],
    optionalRoles: ['cooldown_support']
  },
  blood_pact: {
    primaryRole: 'linked_survivor',
    secondaryRoles: ['teammate_locator', 'stay_together', 'shared_haste'],
    optionalRoles: ['post_heal_link']
  },
  blood_rush: {
    primaryRole: 'exhaustion_info',
    secondaryRoles: [],
    optionalRoles: []
  },
  bond: {
    primaryRole: 'teammate_locator',
    secondaryRoles: ['aura_information'],
    optionalRoles: []
  },
  built_to_last: {
    primaryRole: 'brought_item_economy',
    secondaryRoles: ['charge_efficiency', 'item_recovery'],
    optionalRoles: []
  },
  counterforce: {
    primaryRole: 'totem_cleanse_speed',
    secondaryRoles: ['totem_interaction'],
    optionalRoles: []
  },
  dance_with_me: {
    primaryRole: 'post_vault_disappearance',
    secondaryRoles: ['disappearance_variant'],
    optionalRoles: []
  },
  deja_vu: {
    primaryRole: 'objective_routing',
    secondaryRoles: ['generator_macro', 'map_routing', 'aura_information'],
    optionalRoles: []
  },
  detectives_hunch: {
    primaryRole: 'objective_routing',
    secondaryRoles: ['generator_macro', 'map_routing', 'aura_information', 'totem_interaction'],
    optionalRoles: []
  },
  empathy: {
    primaryRole: 'teammate_locator',
    secondaryRoles: ['heal_reset'],
    optionalRoles: []
  },
  extrasensory_perception: {
    primaryRole: 'crouch_recon',
    secondaryRoles: ['aura_information', 'resource_info', 'stealth'],
    optionalRoles: ['map_routing', 'totem_interaction']
  },
  eyes_of_belmont: {
    primaryRole: 'killer_aura_duration_extension',
    secondaryRoles: ['aura_information'],
    optionalRoles: []
  },
  finesse: {
    primaryRole: 'vault_speed',
    secondaryRoles: ['chase_extension'],
    optionalRoles: []
  },
  fixated: {
    primaryRole: 'walking_reposition',
    secondaryRoles: ['chase_reposition', 'map_routing'],
    optionalRoles: []
  },
  ghost_notes: {
    primaryRole: 'scratch_mark_disappearance',
    secondaryRoles: ['scratch_mark_denial', 'line_of_sight_break'],
    optionalRoles: ['minor_exhaustion_recovery', 'cooldown_support']
  },
  head_on: {
    primaryRole: 'locker_trigger',
    secondaryRoles: ['exhaustion_spender'],
    optionalRoles: []
  },
  iron_will: {
    primaryRole: 'noise_denial',
    secondaryRoles: ['line_of_sight_break'],
    optionalRoles: []
  },
  kindred: {
    primaryRole: 'hook_control',
    secondaryRoles: ['post_unhook_survival', 'resource_info'],
    optionalRoles: []
  },
  lightweight: {
    primaryRole: 'scratch_mark_denial',
    secondaryRoles: ['line_of_sight_break'],
    optionalRoles: []
  },
  lithe: {
    primaryRole: 'exhaustion_vault_chase',
    secondaryRoles: ['vault_trigger', 'chase_reposition'],
    optionalRoles: ['post_vault_disappearance']
  },
  lucky_break: {
    primaryRole: 'scratch_mark_denial',
    secondaryRoles: ['line_of_sight_break'],
    optionalRoles: []
  },
  off_the_record: {
    primaryRole: 'post_unhook_survival',
    secondaryRoles: ['anti_tunnel', 'noise_denial'],
    optionalRoles: []
  },
  open_handed: {
    primaryRole: 'aura_extension',
    secondaryRoles: ['aura_information'],
    optionalRoles: ['teamplay']
  },
  plunderers_instinct: {
    primaryRole: 'chest_finding',
    secondaryRoles: ['chest_loot'],
    optionalRoles: []
  },
  quick_and_quiet: {
    primaryRole: 'noise_denial',
    secondaryRoles: ['vault_trigger', 'post_vault_disappearance'],
    optionalRoles: []
  },
  rapid_response: {
    primaryRole: 'locker_exhaustion_info',
    secondaryRoles: ['locker_trigger', 'exhaustion_info', 'chase_reposition'],
    optionalRoles: []
  },
  resilience: {
    primaryRole: 'chase_extension',
    secondaryRoles: ['vault_speed'],
    optionalRoles: []
  },
  resurgence: {
    primaryRole: 'post_unhook_survival',
    secondaryRoles: ['heal_reset'],
    optionalRoles: []
  },
  scavenger: {
    primaryRole: 'item_recovery',
    secondaryRoles: ['toolbox_or_medkit_value'],
    optionalRoles: []
  },
  specialist: {
    primaryRole: 'chest_loot',
    secondaryRoles: ['token_support', 'repair_progress_or_skillcheck', 'generator_macro'],
    optionalRoles: []
  },
  self_preservation: {
    primaryRole: 'scratch_mark_denial',
    secondaryRoles: ['line_of_sight_break'],
    optionalRoles: []
  },
  slippery_meat: {
    primaryRole: 'self_unhook_attempts',
    secondaryRoles: ['luck'],
    optionalRoles: []
  },
  sprint_burst: {
    primaryRole: 'exhaustion_spender',
    secondaryRoles: ['chase_reposition'],
    optionalRoles: []
  },
  still_sight: {
    primaryRole: 'stationary_recon',
    secondaryRoles: ['aura_information', 'generator_macro'],
    optionalRoles: []
  },
  streetwise: {
    primaryRole: 'chest_loot',
    secondaryRoles: ['item_quality', 'aura_information'],
    optionalRoles: ['item_conversion']
  },
  change_of_plan: {
    primaryRole: 'item_recovery',
    secondaryRoles: ['toolbox_or_medkit_value'],
    optionalRoles: []
  },
  teamwork_collective_stealth: {
    primaryRole: 'duo_proximity',
    secondaryRoles: ['stay_together', 'post_heal_link', 'line_of_sight_break'],
    optionalRoles: []
  },
  teamwork_power_of_two: {
    primaryRole: 'duo_proximity',
    secondaryRoles: ['stay_together', 'shared_haste', 'post_heal_link'],
    optionalRoles: []
  },
  up_the_ante: {
    primaryRole: 'hook_luck_gamble',
    secondaryRoles: ['luck'],
    optionalRoles: ['hook_control']
  },
  urban_evasion: {
    primaryRole: 'crouch_mobility',
    secondaryRoles: ['stealth'],
    optionalRoles: []
  },
  vigil: {
    primaryRole: 'status_exhaustion_battery',
    secondaryRoles: ['cooldown_support', 'exhaustion_recovery', 'exhaustion_info'],
    optionalRoles: []
  },
  visionary: {
    primaryRole: 'objective_routing',
    secondaryRoles: ['generator_macro', 'aura_information'],
    optionalRoles: []
  },
  windows_of_opportunity: {
    primaryRole: 'pallet_window_routing',
    secondaryRoles: ['resource_info', 'map_routing'],
    optionalRoles: []
  },
  clairvoyance: {
    primaryRole: 'totem_consume_aura',
    secondaryRoles: ['resource_info', 'map_routing'],
    optionalRoles: ['aura_information']
  },
  reactive_healing: {
    primaryRole: 'reactive_heal_link',
    secondaryRoles: ['heal_reset', 'stay_together'],
    optionalRoles: ['teammate_locator']
  },
  wide_open_throttle: {
    primaryRole: 'pallet_vault_trigger',
    secondaryRoles: ['chase_reposition', 'pallet_play', 'vault_trigger'],
    optionalRoles: ['chase_extension']
  },
  lend_a_hand: {
    primaryRole: 'totem_heal_charge_grant',
    secondaryRoles: ['totem_interaction', 'heal_reset', 'teamplay'],
    optionalRoles: ['teammate_locator']
  },
  cross_examination: {
    primaryRole: 'killer_path_tracking',
    secondaryRoles: ['terror_radius_value', 'stealth', 'chase_reposition'],
    optionalRoles: ['resource_info']
  },
  wicked: {
    primaryRole: 'basement_self_unhook',
    secondaryRoles: ['self_unhook_attempts', 'post_unhook_survival'],
    optionalRoles: ['hook_control']
  }
}

function createDefaultRole (perkId) {
  const tags = survivorTagOverrides[perkId] || []
  const mappedRoles = tags.map(tag => ROLE_BY_TAG[tag] || tag)
  return {
    primaryRole: mappedRoles[0] || 'general_utility',
    secondaryRoles: mappedRoles.slice(1),
    optionalRoles: []
  }
}

export const survivorPerkRoles = Object.keys(survivorTagOverrides).reduce((roles, perkId) => {
  roles[perkId] = Object.assign(createDefaultRole(perkId), ROLE_OVERRIDES[perkId])
  return roles
}, {})

const CENTRAL_PROFILE_OVERRIDES = {
  ace_in_the_hole: {
    archetype: 'chest_loot',
    requiredRoles: ['chest_finding', 'item_quality'],
    preferredRoles: ['item_conversion']
  },
  balanced_landing: {
    archetype: 'vertical_exhaustion_chase',
    requiredRoles: ['resource_info', 'map_routing', 'chase_reposition'],
    preferredRoles: ['cooldown_support'],
    forbiddenRoles: ['post_vault_disappearance', 'scratch_mark_denial']
  },
  bite_the_bullet: {
    archetype: 'heal_noise_denial',
    requiredRoles: ['heal_reset', 'noise_denial'],
    preferredRoles: ['line_of_sight_break']
  },
  blood_pact: {
    archetype: 'linked_survivor',
    requiredRoles: ['stay_together', 'shared_haste'],
    preferredRoles: ['post_heal_link', 'teammate_locator']
  },
  built_to_last: {
    archetype: 'brought_item_economy',
    requiredRoles: ['charge_efficiency', 'item_recovery'],
    preferredRoles: ['toolbox_or_medkit_value'],
    forbiddenRoles: ['chest_loot']
  },
  clairvoyance: {
    archetype: 'totem_consume_aura',
    requiredRoles: ['totem_interaction', 'resource_info'],
    preferredRoles: ['map_routing', 'aura_information'],
    forbiddenRoles: ['chest_loot']
  },
  dance_with_me: {
    archetype: 'post_vault_disappearance',
    requiredRoles: ['vault_trigger', 'noise_denial', 'scratch_mark_denial'],
    preferredRoles: ['line_of_sight_break'],
    forbiddenRoles: ['generic_chase_comfort']
  },
  eyes_of_belmont: {
    archetype: 'timed_killer_aura_extension',
    requiredRoles: ['timed_killer_aura', 'skillcheck_value'],
    preferredRoles: ['token_support', 'repair_progress_or_skillcheck'],
    forbiddenRoles: ['aura_extension']
  },
  ghost_notes: {
    archetype: 'scratch_mark_disappearance',
    requiredRoles: ['scratch_mark_denial', 'line_of_sight_break'],
    preferredRoles: ['noise_denial', 'stealth_tracking_denial'],
    forbiddenRoles: ['exhaustion_battery_core']
  },
  lithe: {
    archetype: 'exhaustion_vault_chase',
    requiredRoles: ['vault_speed', 'pallet_window_routing', 'chase_extension'],
    preferredRoles: ['resource_info', 'map_routing'],
    forbiddenRoles: ['post_vault_disappearance_stack', 'chest_loot', 'totem_consume']
  },
  off_the_record: {
    archetype: 'post_unhook_survival',
    requiredRoles: ['anti_tunnel', 'noise_denial'],
    preferredRoles: ['chase_reposition', 'heal_reset']
  },
  rapid_response: {
    archetype: 'locker_exhaustion_info',
    requiredRoles: ['locker_trigger', 'exhaustion_info'],
    preferredRoles: ['chase_reposition', 'resource_info']
  },
  specialist: {
    archetype: 'specialist_chest_skillcheck',
    requiredRoles: ['chest_loot', 'skillcheck_value'],
    preferredRoles: ['token_support', 'repair_progress_or_skillcheck', 'generator_macro']
  },
  reactive_healing: {
    archetype: 'reactive_heal_link',
    requiredRoles: ['heal_reset', 'stay_together'],
    preferredRoles: ['teammate_locator', 'post_heal_link']
  },
  up_the_ante: {
    archetype: 'hook_luck_gamble',
    requiredRoles: ['luck', 'self_unhook_attempts'],
    preferredRoles: ['post_unhook_survival', 'hook_control']
  },
  vigil: {
    archetype: 'status_exhaustion_battery',
    requiredRoles: ['exhaustion_spender', 'cooldown_support'],
    preferredRoles: ['exhaustion_info', 'chase_reposition']
  },
  wicked: {
    archetype: 'basement_self_unhook',
    requiredRoles: ['self_unhook_attempts', 'post_unhook_survival'],
    preferredRoles: ['hook_control']
  },
  wide_open_throttle: {
    archetype: 'pallet_vault_chase',
    requiredRoles: ['pallet_window_routing', 'chase_reposition'],
    preferredRoles: ['resource_info']
  },
  lend_a_hand: {
    archetype: 'totem_heal_support',
    requiredRoles: ['totem_interaction', 'heal_reset'],
    preferredRoles: ['teammate_locator', 'teamplay']
  },
  cross_examination: {
    archetype: 'light_mark_tracking',
    requiredRoles: ['killer_path_tracking', 'terror_radius_value'],
    preferredRoles: ['chase_reposition', 'stealth']
  }
}

const ARCHETYPE_PROFILE_OVERRIDES = {
  anti_slug_recovery: {
    requiredRoles: ['anti_slug', 'recovery_speed'],
    preferredRoles: ['pickup_denial', 'post_unhook_survival'],
    forbiddenRoles: ['chest_loot']
  },
  anti_slug_mobility: {
    requiredRoles: ['anti_slug', 'disappearance'],
    preferredRoles: ['recovery_speed', 'pickup_denial'],
    forbiddenRoles: ['chest_loot']
  },
  anti_slug_save: {
    requiredRoles: ['anti_slug', 'save'],
    preferredRoles: ['recovery_speed', 'teamplay'],
    forbiddenRoles: ['chest_loot']
  },
  anti_tunnel_chase: {
    requiredRoles: ['anti_tunnel', 'chase_reposition'],
    preferredRoles: ['post_unhook_survival', 'exhaustion_spender'],
    forbiddenRoles: ['chest_loot', 'totem_consume']
  },
  anti_tunnel_pickup: {
    requiredRoles: ['anti_tunnel', 'post_unhook_survival'],
    preferredRoles: ['exhaustion_spender', 'chase_reposition', 'heal_reset'],
    forbiddenRoles: ['chest_loot']
  },
  autonomous_reset: {
    requiredRoles: ['heal_reset', 'line_of_sight_break'],
    preferredRoles: ['anti_slug', 'injured_value', 'scratch_mark_denial'],
    forbiddenRoles: ['chest_loot']
  },
  basement_heal_reset: {
    requiredRoles: ['basement', 'heal_reset'],
    preferredRoles: ['aura_information', 'line_of_sight_break'],
    forbiddenRoles: ['chest_loot']
  },
  basement_invocation_aura: {
    requiredRoles: ['basement', 'aura_information'],
    preferredRoles: ['teamplay', 'totem_interaction'],
    forbiddenRoles: ['chest_loot']
  },
  basement_invocation_gen: {
    requiredRoles: ['basement', 'generator_macro'],
    preferredRoles: ['repair_progress_or_skillcheck', 'teamplay'],
    forbiddenRoles: ['chest_loot']
  },
  carried_wiggle_plan: {
    requiredRoles: ['pickup_denial', 'anti_slug'],
    preferredRoles: ['pallet_play', 'map_routing', 'save'],
    forbiddenRoles: ['chest_loot']
  },
  carried_wiggle_save: {
    requiredRoles: ['pickup_denial', 'save'],
    preferredRoles: ['anti_slug', 'teammate_locator'],
    forbiddenRoles: ['chest_loot']
  },
  chase_aura_reveal: {
    requiredRoles: ['aura_information', 'chase'],
    preferredRoles: ['chase_reposition', 'line_of_sight_break'],
    forbiddenRoles: ['chest_loot']
  },
  chase_gen_info: {
    requiredRoles: ['chase', 'generator_macro'],
    preferredRoles: ['aura_information', 'pallet_window_routing'],
    forbiddenRoles: ['chest_loot']
  },
  chase_routing_info: {
    requiredRoles: ['pallet_window_routing', 'map_routing'],
    preferredRoles: ['chase_extension', 'vault_speed'],
    forbiddenRoles: ['chest_loot']
  },
  chase_team_gen: {
    requiredRoles: ['chase_extension', 'generator_macro'],
    preferredRoles: ['pallet_window_routing', 'aura_information', 'teamplay'],
    forbiddenRoles: ['chest_loot']
  },
  chaotic_item_exhaustion: {
    requiredRoles: ['exhaustion_spender', 'item_economy'],
    preferredRoles: ['cooldown_support', 'chase_reposition'],
    forbiddenRoles: ['post_vault_disappearance_stack']
  },
  emergency_heal_save: {
    requiredRoles: ['heal_reset', 'save'],
    preferredRoles: ['obsession', 'anti_tunnel', 'teammate_locator'],
    forbiddenRoles: ['chest_loot']
  },
  endgame_chase: {
    requiredRoles: ['endgame', 'chase_reposition'],
    preferredRoles: ['gate_info', 'teamplay'],
    forbiddenRoles: ['chest_loot', 'totem_consume']
  },
  endgame_gate: {
    requiredRoles: ['endgame', 'gate_info'],
    preferredRoles: ['aura_information', 'teamplay'],
    forbiddenRoles: ['chest_loot']
  },
  endgame_swing: {
    requiredRoles: ['endgame', 'chase_reposition'],
    preferredRoles: ['anti_tunnel', 'heal_reset'],
    forbiddenRoles: ['chest_loot']
  },
  general_exhaustion_chase: {
    requiredRoles: ['exhaustion_spender', 'chase_reposition'],
    preferredRoles: ['cooldown_support', 'pallet_window_routing'],
    forbiddenRoles: ['post_vault_disappearance_stack', 'chest_loot']
  },
  sprint_burst_control: {
    requiredRoles: ['walking_reposition', 'cooldown_support'],
    preferredRoles: ['pallet_window_routing', 'resource_info', 'map_routing'],
    forbiddenRoles: ['exhaustion_spender', 'post_vault_disappearance', 'chest_loot']
  },
  timed_killer_aura_extension: {
    requiredRoles: ['timed_killer_aura', 'skillcheck_value'],
    preferredRoles: ['token_support', 'repair_progress_or_skillcheck', 'generator_macro'],
    forbiddenRoles: ['aura_extension']
  },
  gen_aura_trap: {
    requiredRoles: ['generator_macro', 'aura_information'],
    preferredRoles: ['gen_bait', 'teamplay'],
    forbiddenRoles: ['chest_loot']
  },
  gen_trap_bait: {
    requiredRoles: ['generator_macro', 'gen_bait'],
    preferredRoles: ['aura_information', 'misdirection'],
    forbiddenRoles: ['chest_loot']
  },
  gen_charge_storage: {
    requiredRoles: ['generator_macro', 'repair_progress_or_skillcheck'],
    preferredRoles: ['aura_information', 'gen_protection'],
    forbiddenRoles: ['chest_loot']
  },
  gen_coordination: {
    requiredRoles: ['generator_macro', 'teamplay'],
    preferredRoles: ['aura_information', 'repair_progress_or_skillcheck'],
    forbiddenRoles: ['chest_loot']
  },
  gen_macro_routing: {
    requiredRoles: ['generator_macro', 'aura_information'],
    preferredRoles: ['repair_speed', 'map_routing'],
    forbiddenRoles: ['chest_loot']
  },
  boon_recon_setup: {
    requiredRoles: ['boon_setup', 'boon_setup'],
    preferredRoles: ['exhaustion_spender', 'chase_reposition', 'pallet_window_routing'],
    forbiddenRoles: ['totem_cleanse_speed']
  },
  crouch_omniscience: {
    requiredRoles: ['aura_extension', 'crouch_mobility'],
    preferredRoles: ['exhaustion_spender', 'chase_reposition', 'pallet_window_routing'],
    forbiddenRoles: ['passive_recon']
  },
  heal_stealth_reset: {
    requiredRoles: ['heal_reset', 'teammate_locator'],
    preferredRoles: ['post_heal_link', 'line_of_sight_break', 'aura_information'],
    forbiddenRoles: ['chest_loot']
  },
  gen_misdirection: {
    requiredRoles: ['generator_macro', 'misdirection'],
    preferredRoles: ['stealth', 'locker_trigger'],
    forbiddenRoles: ['pure_chase_core']
  },
  gen_progress_protection: {
    requiredRoles: ['generator_macro', 'gen_protection'],
    preferredRoles: ['aura_information', 'gen_bait'],
    forbiddenRoles: ['chest_loot']
  },
  gen_skillcheck_engine: {
    requiredRoles: ['skillcheck_value', 'repair_progress_or_skillcheck'],
    preferredRoles: ['token_support', 'generator_macro'],
    forbiddenRoles: ['chest_loot']
  },
  specialist_chest_skillcheck: {
    requiredRoles: ['chest_loot', 'skillcheck_value'],
    preferredRoles: ['token_support', 'repair_progress_or_skillcheck', 'generator_macro'],
    forbiddenRoles: []
  },
  blind_item_support: {
    requiredRoles: ['item_economy', 'save'],
    preferredRoles: ['pickup_denial', 'aura_information'],
    forbiddenRoles: []
  },
  flashlight_chase_save: {
    requiredRoles: ['item_economy', 'chase'],
    preferredRoles: ['save', 'pickup_denial'],
    forbiddenRoles: ['chest_loot']
  },
  gen_completion_stealth: {
    requiredRoles: ['generator_macro', 'disappearance'],
    preferredRoles: ['stealth', 'aura_information'],
    forbiddenRoles: ['chest_loot']
  },
  gen_team_repair: {
    requiredRoles: ['generator_macro', 'teamplay'],
    preferredRoles: ['repair_speed', 'teammate_locator'],
    forbiddenRoles: ['chest_loot']
  },
  gen_totem_payoff: {
    requiredRoles: ['totem_interaction', 'generator_macro'],
    preferredRoles: ['repair_speed', 'aura_information'],
    forbiddenRoles: ['chest_loot']
  },
  hardmode_injured: {
    requiredRoles: ['injured_value', 'anti_slug'],
    preferredRoles: ['line_of_sight_break', 'recovery_speed'],
    forbiddenRoles: ['standard_heal_plan', 'healthy_only']
  },
  hit_disappearance: {
    requiredRoles: ['line_of_sight_break', 'scratch_mark_denial'],
    preferredRoles: ['noise_denial', 'chase_reposition', 'heal_reset'],
    forbiddenRoles: ['chest_loot']
  },
  heal_skillcheck_scaling: {
    requiredRoles: ['heal_reset', 'skillcheck_value'],
    preferredRoles: ['teammate_locator', 'long_heal_window'],
    forbiddenRoles: ['chest_loot']
  },
  heal_speed_reset: {
    requiredRoles: ['heal_reset', 'heal_speed'],
    preferredRoles: ['teammate_locator', 'safe_reset'],
    forbiddenRoles: ['chest_loot']
  },
  heal_support_buff: {
    requiredRoles: ['heal_reset', 'teamplay'],
    preferredRoles: ['teammate_locator', 'support_action'],
    forbiddenRoles: ['chest_loot']
  },
  hook_denial_save: {
    requiredRoles: ['save', 'pickup_denial'],
    preferredRoles: ['hook_control', 'teammate_locator'],
    forbiddenRoles: ['chest_loot']
  },
  hook_info_flow: {
    requiredRoles: ['hook_control', 'aura_information'],
    preferredRoles: ['save', 'teammate_locator'],
    forbiddenRoles: ['chest_loot']
  },
  hook_stall_save: {
    requiredRoles: ['hook_control', 'save'],
    preferredRoles: ['anti_tunnel', 'teamplay'],
    forbiddenRoles: ['chest_loot']
  },
  injured_action_value: {
    requiredRoles: ['injured_value', 'action_speed'],
    preferredRoles: ['vault_speed', 'generator_macro'],
    forbiddenRoles: ['standard_heal_plan']
  },
  injured_chase_support: {
    requiredRoles: ['injured_value', 'chase'],
    preferredRoles: ['heal_reset', 'save', 'anti_tunnel'],
    forbiddenRoles: ['standard_heal_plan']
  },
  injured_locator: {
    requiredRoles: ['teammate_locator', 'heal_reset'],
    preferredRoles: ['save', 'teamplay'],
    forbiddenRoles: ['chest_loot']
  },
  item_gen_conversion: {
    requiredRoles: ['item_economy', 'generator_macro'],
    preferredRoles: ['item_recovery', 'repair_progress_or_skillcheck'],
    forbiddenRoles: []
  },
  item_gen_recovery: {
    requiredRoles: ['item_economy', 'item_recovery'],
    preferredRoles: ['generator_macro', 'repair_progress_or_skillcheck'],
    forbiddenRoles: []
  },
  locker_ambush: {
    requiredRoles: ['locker_trigger', 'chase'],
    preferredRoles: ['noise_denial', 'exhaustion_recovery'],
    forbiddenRoles: ['chest_loot']
  },
  locker_misdirection: {
    requiredRoles: ['locker_trigger', 'misdirection'],
    preferredRoles: ['stealth', 'noise_denial'],
    forbiddenRoles: ['pure_chase_core']
  },
  locker_stealth_info: {
    requiredRoles: ['locker_trigger', 'stealth'],
    preferredRoles: ['aura_information', 'noise_denial'],
    forbiddenRoles: ['pure_chase_core']
  },
  misdirection: {
    requiredRoles: ['misdirection', 'stealth'],
    preferredRoles: ['locker_trigger', 'generator_macro'],
    forbiddenRoles: ['pure_chase_core']
  },
  noise_stealth_escape: {
    requiredRoles: ['noise_denial', 'line_of_sight_break'],
    preferredRoles: ['scratch_mark_denial', 'injured_value'],
    forbiddenRoles: ['chest_loot']
  },
  objective_aura_resilience: {
    requiredRoles: ['aura_information', 'objective_interaction'],
    preferredRoles: ['totem_interaction', 'item_economy'],
    forbiddenRoles: ['chest_loot']
  },
  obsession_aura_value: {
    requiredRoles: ['obsession', 'aura_information'],
    preferredRoles: ['action_speed', 'anti_tunnel'],
    forbiddenRoles: ['chest_loot']
  },
  pallet_exhaustion_chase: {
    requiredRoles: ['pallet_play', 'exhaustion_spender'],
    preferredRoles: ['chase_extension', 'pallet_window_routing'],
    forbiddenRoles: ['chest_loot']
  },
  pallet_item_upgrade: {
    requiredRoles: ['pallet_play', 'item_economy'],
    preferredRoles: ['chase_extension', 'resource_info'],
    forbiddenRoles: ['chest_loot']
  },
  pallet_pickup_denial: {
    requiredRoles: ['pallet_play', 'pickup_denial'],
    preferredRoles: ['anti_slug', 'map_routing'],
    forbiddenRoles: ['chest_loot']
  },
  pallet_resource_control: {
    requiredRoles: ['pallet_play', 'resource_info'],
    preferredRoles: ['chase_extension', 'map_routing'],
    forbiddenRoles: ['chest_loot']
  },
  pallet_trap_chase: {
    requiredRoles: ['pallet_play', 'chase'],
    preferredRoles: ['gen_bait', 'map_routing'],
    forbiddenRoles: ['chest_loot']
  },
  pickup_intervention: {
    requiredRoles: ['pickup_denial', 'save'],
    preferredRoles: ['teammate_locator', 'item_economy', 'chase_reposition'],
    forbiddenRoles: ['chest_loot']
  },
  post_unhook_exhaustion_reset: {
    requiredRoles: ['post_unhook_survival', 'exhaustion_recovery'],
    preferredRoles: ['exhaustion_spender', 'anti_tunnel'],
    forbiddenRoles: ['chest_loot']
  },
  protection_hit_save: {
    requiredRoles: ['save', 'protection_hit'],
    preferredRoles: ['anti_tunnel', 'chase_reposition'],
    forbiddenRoles: ['chest_loot']
  },
  self_unhook_plan: {
    requiredRoles: ['self_unhook_attempts', 'post_unhook_survival'],
    preferredRoles: ['hook_control', 'save'],
    forbiddenRoles: ['chest_loot']
  },
  team_aura_save: {
    requiredRoles: ['aura_information', 'save'],
    preferredRoles: ['teamplay', 'teammate_locator'],
    forbiddenRoles: ['chest_loot']
  },
  unhook_heal_reset: {
    requiredRoles: ['save', 'heal_reset'],
    preferredRoles: ['hook_control', 'teammate_locator'],
    forbiddenRoles: ['chest_loot']
  },
  skillcheck_aura_info: {
    requiredRoles: ['skillcheck_value', 'aura_information'],
    preferredRoles: ['generator_macro', 'repair_progress_or_skillcheck'],
    forbiddenRoles: ['chest_loot']
  },
  skillcheck_team_heal: {
    requiredRoles: ['skillcheck_value', 'teamplay'],
    preferredRoles: ['heal_reset', 'generator_macro'],
    forbiddenRoles: ['chest_loot']
  },
  skillcheck_token_value: {
    requiredRoles: ['skillcheck_value', 'token_support'],
    preferredRoles: ['generator_macro', 'heal_reset'],
    forbiddenRoles: ['chest_loot']
  },
  stealth_recon_conversion: {
    requiredRoles: ['crouch_mobility', 'generator_macro'],
    preferredRoles: ['chase_reposition', 'pallet_window_routing', 'scratch_mark_denial'],
    forbiddenRoles: ['passive_stealth_stack']
  },
  stealth_action_denial: {
    requiredRoles: ['noise_denial', 'disappearance'],
    preferredRoles: ['locker_trigger', 'line_of_sight_break'],
    forbiddenRoles: ['pure_chase_core']
  },
  stealth_aura_denial: {
    requiredRoles: ['stealth', 'aura_information'],
    preferredRoles: ['line_of_sight_break', 'scratch_mark_denial'],
    forbiddenRoles: ['chest_loot']
  },
  stun_disappearance: {
    requiredRoles: ['pallet_play', 'disappearance'],
    preferredRoles: ['stun', 'line_of_sight_break'],
    forbiddenRoles: ['chest_loot']
  },
  teammate_info_flow: {
    requiredRoles: ['teammate_locator', 'teamplay'],
    preferredRoles: ['heal_reset', 'generator_macro'],
    forbiddenRoles: ['chest_loot']
  },
  totem_self_reset: {
    requiredRoles: ['totem_interaction', 'heal_reset'],
    preferredRoles: ['aura_information', 'line_of_sight_break'],
    forbiddenRoles: ['chest_loot']
  },
  trace_denial_escape: {
    requiredRoles: ['scratch_mark_denial', 'line_of_sight_break'],
    preferredRoles: ['noise_denial', 'chase_reposition'],
    forbiddenRoles: ['chest_loot']
  },
  vault_speed_chase: {
    requiredRoles: ['vault_speed', 'chase_extension'],
    preferredRoles: ['pallet_window_routing', 'chase_reposition'],
    forbiddenRoles: ['post_vault_disappearance_stack', 'chest_loot']
  },
  window_trap_chase: {
    requiredRoles: ['vault_trigger', 'chase'],
    preferredRoles: ['pallet_window_routing', 'map_routing'],
    forbiddenRoles: ['chest_loot']
  }
}

const CENTRAL_ARCHETYPE_OVERRIDES = {
  lucky_break: 'hit_disappearance',
  plot_twist: 'autonomous_reset',
  dramaturgy: 'general_exhaustion_chase',
  quick_gambit: 'chase_team_gen',
  background_player: 'pickup_intervention',
  hyperfocus: 'gen_skillcheck_engine',
  potential_energy: 'gen_charge_storage',
  wiretap: 'gen_aura_trap',
  chemical_trap: 'pallet_trap_chase',
  any_means_necessary: 'pallet_resource_control',
  strength_in_shadows: 'basement_heal_reset',
  no_mither: 'hardmode_injured',
  dead_hard: 'anti_tunnel_chase',
  sprint_burst: 'sprint_burst_control',
  finesse: 'vault_speed_chase',
  resilience: 'injured_action_value',
  windows_of_opportunity: 'chase_routing_info',
  bond: 'teammate_info_flow',
  kindred: 'hook_info_flow',
  iron_will: 'noise_stealth_escape',
  decisive_strike: 'anti_tunnel_pickup',
  deliverance: 'self_unhook_plan',
  distortion: 'stealth_aura_denial',
  flashbang: 'pickup_intervention',
  blast_mine: 'gen_trap_bait',
  repressed_alliance: 'gen_progress_protection',
  overzealous: 'gen_totem_payoff',
  inner_strength: 'totem_self_reset',
  head_on: 'locker_ambush',
  quick_and_quiet: 'stealth_action_denial',
  lightweight: 'trace_denial_escape',
  light_footed: 'noise_stealth_escape',
  made_for_this: 'injured_chase_support',
  for_the_people: 'emergency_heal_save',
  buckle_up: 'anti_slug_recovery',
  were_gonna_live_forever: 'anti_slug_save',
  unbreakable: 'anti_slug_recovery',
  tenacity: 'anti_slug_mobility',
  flip_flop: 'carried_wiggle_plan',
  power_struggle: 'pallet_pickup_denial',
  boil_over: 'carried_wiggle_plan',
  saboteur: 'hook_denial_save',
  breakout: 'carried_wiggle_save',
  mettle_of_man: 'protection_hit_save',
  reassurance: 'hook_stall_save',
  well_make_it: 'unhook_heal_reset',
  autodidact: 'heal_skillcheck_scaling',
  botany_knowledge: 'heal_speed_reset',
  empathic_connection: 'heal_support_buff',
  empathy: 'injured_locator',
  fogwise: 'skillcheck_aura_info',
  stake_out: 'skillcheck_token_value',
  scavenger: 'item_gen_recovery',
  specialist: 'specialist_chest_skillcheck',
  appraisal: 'chest_loot',
  pharmacy: 'chest_loot',
  streetwise: 'chest_loot',
  open_handed: 'aura_extension',
  scene_partner: 'chase_aura_reveal',
  extrasensory_perception: 'crouch_omniscience',
  hardened: 'objective_aura_resilience',
  object_of_obsession: 'obsession_aura_value',
  troubleshooter: 'chase_gen_info',
  better_together: 'gen_coordination',
  prove_thyself: 'gen_team_repair',
  deja_vu: 'gen_macro_routing',
  red_herring: 'gen_misdirection',
  mirrored_illusion: 'gen_misdirection',
  diversion: 'misdirection',
  deception: 'locker_misdirection',
  lucky_star: 'locker_stealth_info',
  invocation_weaving_spiders: 'basement_invocation_gen',
  treacherous_crows: 'basement_invocation_aura',
  adrenaline: 'endgame_swing',
  hope: 'endgame_chase',
  wake_up: 'endgame_gate',
  blood_rush: 'post_unhook_exhaustion_reset',
  slippery_meat: 'hook_luck_gamble',
  baby_sitter: 'team_aura_save',
  a_place_for_us: 'heal_stealth_reset',
  bada_bada_boom: 'window_trap_chase',
  five_moves_ahead: 'chase_routing_info',
  parental_guidance: 'stun_disappearance',
  smash_hit: 'pallet_exhaustion_chase',
  champion_of_light: 'flashlight_chase_save',
  residual_manifest: 'blind_item_support',
  exultation: 'pallet_item_upgrade',
  road_life: 'skillcheck_team_heal',
  poised: 'gen_completion_stealth',
  we_see_you: 'team_aura_save',
  better_than_new: 'heal_support_buff',
  teamwork_power_of_two: 'duo_proximity',
  teamwork_collective_stealth: 'duo_proximity',
  teamwork_full_circuit: 'gen_coordination',
  teamwork_soft_spoken: 'gen_coordination',
  teamwork_throw_down: 'duo_proximity',
  teamwork_toughen_up: 'duo_proximity'
}

function createProfileOverrideFromArchetype (archetype) {
  const archetypeProfile = ARCHETYPE_PROFILE_OVERRIDES[archetype] || {}
  return Object.assign({ archetype: archetype }, archetypeProfile)
}

const CENTRAL_ARCHETYPE_PROFILE_OVERRIDES = Object.keys(CENTRAL_ARCHETYPE_OVERRIDES).reduce((profiles, perkId) => {
  profiles[perkId] = createProfileOverrideFromArchetype(CENTRAL_ARCHETYPE_OVERRIDES[perkId])
  return profiles
}, {})

const EXPLICIT_CENTRAL_PROFILE_OVERRIDES = Object.assign(
  {},
  CENTRAL_ARCHETYPE_PROFILE_OVERRIDES,
  CENTRAL_PROFILE_OVERRIDES
)

function createDefaultCentralProfile (perkId) {
  const role = survivorPerkRoles[getSurvivorGameplayPerkId(perkId)]
  return {
    archetype: role.primaryRole,
    requiredRoles: role.secondaryRoles.length ? role.secondaryRoles.slice(0, 2) : [role.primaryRole],
    preferredRoles: role.optionalRoles.slice(),
    allowedRoles: [role.primaryRole].concat(role.secondaryRoles, role.optionalRoles),
    forbiddenRoles: [],
    allowLegacyFallback: false
  }
}

export const survivorCentralProfiles = Object.keys(survivorPerkRoles).reduce((profiles, perkId) => {
  profiles[perkId] = Object.assign(createDefaultCentralProfile(perkId), EXPLICIT_CENTRAL_PROFILE_OVERRIDES[perkId])
  return profiles
}, {})

const MANUAL_CENTRAL_WANTS = {
  a_place_for_us: {
    aftercare: 88,
    empathic_connection: 82,
    botany_knowledge: 76,
    blood_pact: 72,
    empathy: 68,
    bite_the_bullet: 62
  },
  ace_in_the_hole: {
    plunderers_instinct: 96,
    appraisal: 90,
    specialist: 82,
    pharmacy: 76,
    streetwise: 68,
    built_to_last: 62
  },
  built_to_last: {
    residual_manifest: 88,
    pharmacy: 82,
    streetwise: 68,
    ace_in_the_hole: 64,
    plunderers_instinct: 60
  },
  lucky_break: {
    iron_will: 92,
    bite_the_bullet: 88,
    quick_and_quiet: 84,
    dance_with_me: 80,
    lightweight: 78,
    overcome: 70,
    lithe: 62,
    plot_twist: 58
  },
  plot_twist: {
    lucky_break: 86,
    quick_and_quiet: 78,
    lightweight: 72,
    distortion: 70,
    tenacity: 68,
    unbreakable: 65,
    flip_flop: 58,
    power_struggle: 55,
    resilience: 50
  },
  dramaturgy: {
    vigil: 88,
    finesse: 86,
    windows_of_opportunity: 78,
    fixated: 74,
    troubleshooter: 70
  },
  quick_gambit: {
    windows_of_opportunity: 94,
    finesse: 90,
    lithe: 86,
    sprint_burst: 82,
    resilience: 78,
    bond: 68,
    better_together: 66,
    wiretap: 62,
    deja_vu: 60
  },
  background_player: {
    flashbang: 96,
    champion_of_light: 90,
    residual_manifest: 82,
    saboteur: 82,
    breakout: 78,
    mettle_of_man: 68,
    bond: 64,
    empathy: 62
  },
  hyperfocus: {
    stake_out: 96,
    fogwise: 78,
    deadline: 64,
    bardic_inspiration: 62,
    corrective_action: 58
  },
  potential_energy: {
    deja_vu: 78,
    wiretap: 72,
    repressed_alliance: 70,
    stake_out: 62,
    hyperfocus: 58
  },
  wiretap: {
    blast_mine: 92,
    repressed_alliance: 78,
    red_herring: 72,
    better_together: 70,
    deja_vu: 64
  },
  chemical_trap: {
    windows_of_opportunity: 88,
    any_means_necessary: 72,
    five_moves_ahead: 70,
    troubleshooter: 68,
    lithe: 58
  },
  any_means_necessary: {
    windows_of_opportunity: 84,
    five_moves_ahead: 78,
    chemical_trap: 74,
    smash_hit: 60
  },
  strength_in_shadows: {
    botany_knowledge: 78,
    bite_the_bullet: 74,
    lucky_break: 66,
    wicked: 62
  },
  no_mither: {
    resilience: 88,
    dead_hard: 82,
    tenacity: 78,
    plot_twist: 72,
    soul_guard: 62,
    deadline: 60,
    power_struggle: 58,
    flip_flop: 55
  },
  dead_hard: {
    decisive_strike: 72,
    off_the_record: 70,
    blood_rush: 70,
    resilience: 64,
    vigil: 52
  },
  sprint_burst: {
    vigil: 96,
    fixated: 82,
    blood_rush: 65,
    windows_of_opportunity: 55
  },
  finesse: {
    lithe: 90,
    windows_of_opportunity: 88,
    resilience: 82,
    five_moves_ahead: 78,
    troubleshooter: 70
  },
  resilience: {
    finesse: 84,
    lithe: 78,
    windows_of_opportunity: 70,
    dead_hard: 65,
    made_for_this: 58
  },
  windows_of_opportunity: {
    lithe: 88,
    finesse: 86,
    balanced_landing: 82,
    smash_hit: 78,
    quick_gambit: 72
  },
  bond: {
    open_handed: 80,
    prove_thyself: 78,
    well_make_it: 72,
    leader: 66,
    background_player: 60
  },
  kindred: {
    open_handed: 96,
    reassurance: 76,
    well_make_it: 70,
    deliverance: 60
  },
  iron_will: {
    lucky_break: 90,
    bite_the_bullet: 78,
    lightweight: 72,
    plot_twist: 65
  },
  decisive_strike: {
    off_the_record: 88,
    dead_hard: 72,
    unbreakable: 70,
    blood_rush: 66,
    resilience: 58
  },
  deliverance: {
    decisive_strike: 76,
    off_the_record: 74,
    kindred: 68,
    reassurance: 55,
    wicked: 45
  },
  distortion: {
    shadow_step: 76,
    calm_spirit: 62,
    lightweight: 60,
    lucky_break: 56,
    plot_twist: 54
  },
  flashbang: {
    background_player: 92,
    champion_of_light: 86,
    residual_manifest: 78,
    empathy: 70,
    bond: 68
  },
  blast_mine: {
    wiretap: 94,
    repressed_alliance: 78,
    red_herring: 76,
    better_together: 68
  },
  repressed_alliance: {
    blast_mine: 86,
    wiretap: 84,
    red_herring: 70,
    deja_vu: 60
  },
  overzealous: {
    counterforce: 92,
    inner_strength: 70,
    clairvoyance: 70,
    detectives_hunch: 65
  },
  inner_strength: {
    overzealous: 78,
    counterforce: 72,
    small_game: 65,
    clairvoyance: 60
  },
  head_on: {
    quick_and_quiet: 96,
    deception: 82,
    lucky_star: 70,
    vigil: 60
  },
  quick_and_quiet: {
    head_on: 92,
    dance_with_me: 86,
    lucky_star: 70,
    deception: 68,
    lithe: 55
  },
  lightweight: {
    lucky_break: 85,
    iron_will: 74,
    dance_with_me: 70,
    quick_and_quiet: 65
  },
  light_footed: {
    dance_with_me: 92,
    quick_and_quiet: 88,
    lightweight: 82,
    iron_will: 76,
    lucky_break: 72,
    fixated: 64,
    lithe: 52
  },
  made_for_this: {
    resilience: 76,
    dead_hard: 66,
    botany_knowledge: 62,
    for_the_people: 58
  },
  for_the_people: {
    buckle_up: 88,
    made_for_this: 70,
    empathy: 68,
    bond: 64
  },
  buckle_up: {
    for_the_people: 90,
    unbreakable: 72,
    were_gonna_live_forever: 68
  },
  unbreakable: {
    soul_guard: 82,
    tenacity: 78,
    flip_flop: 65,
    power_struggle: 60
  },
  tenacity: {
    unbreakable: 88,
    flip_flop: 78,
    power_struggle: 70,
    boil_over: 62
  },
  flip_flop: {
    tenacity: 86,
    power_struggle: 84,
    boil_over: 76,
    unbreakable: 70
  },
  power_struggle: {
    flip_flop: 90,
    tenacity: 78,
    boil_over: 72,
    any_means_necessary: 62
  },
  boil_over: {
    flip_flop: 84,
    tenacity: 72,
    breakdown: 68,
    saboteur: 64
  },
  saboteur: {
    breakout: 88,
    background_player: 78,
    mettle_of_man: 70,
    bond: 64
  },
  breakout: {
    saboteur: 86,
    mettle_of_man: 78,
    background_player: 72,
    empathy: 64
  },
  reassurance: {
    kindred: 80,
    borrowed_time: 68,
    baby_sitter: 64
  },
  well_make_it: {
    botany_knowledge: 82,
    empathy: 74,
    bond: 70,
    kindred: 60
  },
  autodidact: {
    empathy: 82,
    solidarity: 76,
    botany_knowledge: 70,
    deadline: 58
  },
  botany_knowledge: {
    well_make_it: 86,
    empathy: 72,
    bite_the_bullet: 66,
    autodidact: 60
  },
  empathic_connection: {
    botany_knowledge: 75,
    well_make_it: 70,
    empathy: 68,
    aftercare: 64
  },
  empathy: {
    botany_knowledge: 72,
    autodidact: 70,
    background_player: 65,
    breakout: 60
  },
  stake_out: {
    hyperfocus: 92,
    fogwise: 70,
    corrective_action: 62,
    deadline: 58
  },
  scavenger: {
    built_to_last: 82,
    streetwise: 78,
    specialist: 74,
    hyperfocus: 60
  },
  specialist: {
    appraisal: 98,
    stake_out: 94,
    hyperfocus: 90,
    residual_manifest: 88,
    one_two_three_four: 86,
    plunderers_instinct: 84,
    scavenger: 84
  },
  appraisal: {
    ace_in_the_hole: 96,
    plunderers_instinct: 90,
    pharmacy: 72
  },
  pharmacy: {
    appraisal: 92,
    ace_in_the_hole: 86,
    plunderers_instinct: 82,
    streetwise: 72,
    built_to_last: 68,
    specialist: 62
  },
  streetwise: {
    built_to_last: 90,
    scavenger: 74,
    specialist: 70,
    ace_in_the_hole: 60
  },
  open_handed: {
    kindred: 96,
    bond: 74,
    windows_of_opportunity: 60
  },
  object_of_obsession: {
    eyes_of_belmont: 82,
    scene_partner: 70,
    open_handed: 64,
    we_see_you: 62,
    distortion: 58,
    decisive_strike: 54
  },
  troubleshooter: {
    windows_of_opportunity: 78,
    lithe: 72,
    finesse: 68,
    quick_gambit: 65
  },
  better_together: {
    prove_thyself: 82,
    friendly_competition: 76,
    wiretap: 72
  },
  prove_thyself: {
    better_together: 78,
    bond: 70,
    deja_vu: 66,
    friendly_competition: 60
  },
  deja_vu: {
    prove_thyself: 70,
    better_together: 69,
    overzealous: 68,
    wiretap: 64,
    fogwise: 60
  },
  red_herring: {
    blast_mine: 94,
    diversion: 90,
    wiretap: 76,
    deception: 65
  },
  mirrored_illusion: {
    red_herring: 70,
    blast_mine: 64,
    wiretap: 60
  },
  diversion: {
    red_herring: 94,
    blast_mine: 80,
    deception: 82,
    lightweight: 60
  },
  deception: {
    head_on: 78,
    diversion: 74,
    quick_and_quiet: 70
  },
  blood_rush: {
    dead_hard: 82,
    sprint_burst: 78,
    lithe: 76,
    overcome: 72,
    off_the_record: 70,
    decisive_strike: 68,
    vigil: 64
  },
  hope: {
    adrenaline: 88,
    wake_up: 78,
    no_one_left_behind: 72,
    left_behind: 64
  },
  wake_up: {
    hope: 78,
    adrenaline: 72,
    sole_survivor: 65,
    left_behind: 64,
    no_one_left_behind: 60
  },
  adrenaline: {
    hope: 96,
    wake_up: 82,
    no_one_left_behind: 78,
    left_behind: 68,
    sole_survivor: 62,
    low_profile: 58
  },
  apocalyptic_ingenuity: {
    chemical_trap: 86,
    any_means_necessary: 82,
    exultation: 76,
    built_to_last: 66,
    streetwise: 62
  },
  bada_bada_boom: {
    lithe: 90,
    windows_of_opportunity: 86,
    quick_and_quiet: 78,
    dance_with_me: 74,
    finesse: 70
  },
  bardic_inspiration: {
    hyperfocus: 84,
    stake_out: 78,
    fogwise: 74,
    corrective_action: 68,
    deadline: 62,
    prove_thyself: 58
  },
  better_than_new: {
    well_make_it: 86,
    botany_knowledge: 78,
    empathy: 72,
    solidarity: 68,
    leader: 64
  },
  breakdown: {
    off_the_record: 86,
    decisive_strike: 78,
    baby_sitter: 74,
    deliverance: 68,
    reassurance: 62,
    borrowed_time: 58
  },
  calm_spirit: {
    distortion: 82,
    iron_will: 74,
    lightweight: 70,
    lucky_break: 62,
    quick_and_quiet: 58,
    bite_the_bullet: 54
  },
  circle_of_healing: {
    shadow_step: 80,
    exponential: 76,
    dark_theory: 70,
    botany_knowledge: 68,
    empathic_connection: 62
  },
  dark_sense: {
    object_of_obsession: 84,
    eyes_of_belmont: 78,
    open_handed: 74,
    alert: 68,
    troubleshooter: 62,
    scene_partner: 58
  },
  desperate_measures: {
    well_make_it: 84,
    botany_knowledge: 78,
    empathy: 72,
    borrowed_time: 64,
    baby_sitter: 58
  },
  detectives_hunch: {
    inner_strength: 90,
    overzealous: 84,
    counterforce: 78,
    clairvoyance: 72,
    small_game: 68,
    open_handed: 58
  },
  duty_of_care: {
    baby_sitter: 84,
    borrowed_time: 78,
    well_make_it: 72,
    reassurance: 66,
    empathy: 62
  },
  exultation: {
    any_means_necessary: 88,
    chemical_trap: 82,
    apocalyptic_ingenuity: 76,
    dramaturgy: 68,
    appraisal: 62
  },
  exponential: {
    soul_guard: 88,
    unbreakable: 82,
    tenacity: 76,
    flip_flop: 70,
    buckle_up: 64
  },
  fogwise: {
    hyperfocus: 92,
    stake_out: 86,
    corrective_action: 74,
    deadline: 68,
    bardic_inspiration: 64,
    deja_vu: 58
  },
  friendly_competition: {
    prove_thyself: 86,
    better_together: 78,
    deja_vu: 70,
    wiretap: 64,
    leader: 58
  },
  invocation_weaving_spiders: {
    strength_in_shadows: 86,
    no_mither: 72,
    deja_vu: 68,
    prove_thyself: 62,
    resilience: 58
  },
  left_behind: {
    sole_survivor: 90,
    wake_up: 78,
    hope: 72,
    low_profile: 66,
    adrenaline: 58
  },
  mettle_of_man: {
    breakout: 84,
    saboteur: 78,
    background_player: 70,
    for_the_people: 66,
    empathy: 62,
    flashbang: 58
  },
  road_life: {
    botany_knowledge: 82,
    well_make_it: 76,
    empathic_connection: 70,
    autodidact: 66,
    deadline: 60
  },
  scene_partner: {
    object_of_obsession: 86,
    eyes_of_belmont: 80,
    open_handed: 74,
    alert: 68,
    troubleshooter: 62
  },
  second_wind: {
    deliverance: 82,
    off_the_record: 78,
    decisive_strike: 72,
    resurgence: 68,
    well_make_it: 62
  },
  self_preservation: {
    lucky_break: 88,
    lightweight: 82,
    iron_will: 74,
    quick_and_quiet: 68,
    dance_with_me: 62,
    distortion: 56
  },
  shadow_step: {
    circle_of_healing: 84,
    exponential: 78,
    dark_theory: 72,
    distortion: 68,
    lucky_break: 58
  },
  shoulder_the_burden: {
    baby_sitter: 92,
    borrowed_time: 86,
    off_the_record: 82,
    decisive_strike: 76,
    deliverance: 68,
    reassurance: 62,
    well_make_it: 58
  },
  sole_survivor: {
    wake_up: 86,
    left_behind: 84,
    hope: 72,
    low_profile: 66,
    adrenaline: 58
  },
  solidarity: {
    botany_knowledge: 84,
    empathy: 78,
    empathic_connection: 72,
    well_make_it: 68,
    reactive_healing: 64,
    better_than_new: 58
  },
  soul_guard: {
    unbreakable: 90,
    exponential: 84,
    tenacity: 76,
    flip_flop: 70,
    buckle_up: 64,
    were_gonna_live_forever: 58
  },
  spine_chill: {
    resilience: 84,
    lithe: 76,
    windows_of_opportunity: 72,
    sprint_burst: 68,
    finesse: 62
  },
  this_is_not_happening: {
    deadline: 88,
    hyperfocus: 82,
    stake_out: 76,
    autodidact: 70,
    resilience: 64,
    road_life: 58
  },
  treacherous_crows: {
    kindred: 82,
    bond: 76,
    dark_sense: 70,
    fogwise: 64,
    deja_vu: 58
  },
  visionary: {
    deja_vu: 86,
    prove_thyself: 78,
    better_together: 72,
    friendly_competition: 66,
    wiretap: 60
  },
  we_see_you: {
    bond: 86,
    open_handed: 78,
    empathy: 72,
    kindred: 66,
    background_player: 60,
    well_make_it: 56
  },
  aftercare: {
    well_make_it: 82,
    botany_knowledge: 76,
    empathy: 72,
    empathic_connection: 68,
    borrowed_time: 60
  },
  alert: {
    windows_of_opportunity: 82,
    deja_vu: 74,
    troubleshooter: 70,
    dark_sense: 64,
    object_of_obsession: 58
  },
  champion_of_light: {
    residual_manifest: 92,
    flashbang: 86,
    background_player: 80,
    empathy: 66,
    bond: 62
  },
  clean_break: {
    solidarity: 86,
    botany_knowledge: 80,
    well_make_it: 74,
    empathic_connection: 68,
    reactive_healing: 62,
    aftercare: 58
  },
  conviction: {
    tenacity: 88,
    unbreakable: 82,
    soul_guard: 76,
    exponential: 70,
    flip_flop: 64,
    power_struggle: 58
  },
  corrective_action: {
    stake_out: 88,
    hyperfocus: 82,
    bardic_inspiration: 74,
    teamwork_full_circuit: 68,
    better_together: 62,
    technician: 58
  },
  deadline: {
    hyperfocus: 92,
    stake_out: 86,
    this_is_not_happening: 78,
    resilience: 72,
    road_life: 66,
    fogwise: 60
  },
  do_no_harm: {
    botany_knowledge: 86,
    well_make_it: 80,
    empathy: 74,
    autodidact: 68,
    solidarity: 62,
    empathic_connection: 58
  },
  extrasensory_perception: {
    open_handed: 100,
    urban_evasion: 94,
    sprint_burst: 90,
    lithe: 80,
    resilience: 76,
    windows_of_opportunity: 70,
    shadow_step: 62,
    flow_state: 58,
    circle_of_healing: 54
  },
  fast_track: {
    stake_out: 88,
    hyperfocus: 82,
    deadline: 74,
    this_is_not_happening: 68,
    deliverance: 62
  },
  fixated: {
    sprint_burst: 92,
    vigil: 78,
    lightweight: 74,
    urban_evasion: 66,
    lucky_break: 60
  },
  low_profile: {
    sole_survivor: 88,
    left_behind: 82,
    wake_up: 76,
    hope: 70,
    distortion: 64,
    lightweight: 58
  },
  lucky_star: {
    head_on: 90,
    quick_and_quiet: 84,
    deception: 78,
    inner_strength: 72,
    flashbang: 66
  },
  no_one_left_behind: {
    hope: 92,
    adrenaline: 86,
    wake_up: 80,
    borrowed_time: 74,
    baby_sitter: 68,
    well_make_it: 62,
    kindred: 58
  },
  poised: {
    deja_vu: 84,
    dark_sense: 78,
    quick_and_quiet: 72,
    lightweight: 68,
    dance_with_me: 62,
    wiretap: 58
  },
  residual_manifest: {
    champion_of_light: 94,
    flashbang: 88,
    background_player: 82,
    appraisal: 68,
    ace_in_the_hole: 62
  },
  smash_hit: {
    any_means_necessary: 90,
    parental_guidance: 84,
    chemical_trap: 78,
    windows_of_opportunity: 72,
    vigil: 58
  },
  still_sight: {
    clairvoyance: 84,
    visionary: 78,
    urban_evasion: 72,
    open_handed: 66,
    calm_spirit: 62,
    detectives_hunch: 58
  },
  teamwork_soft_spoken: {
    prove_thyself: 84,
    better_together: 78,
    friendly_competition: 72,
    deja_vu: 66,
    leader: 60
  },
  technician: {
    corrective_action: 88,
    stake_out: 78,
    hyperfocus: 72,
    deja_vu: 66,
    fogwise: 62,
    prove_thyself: 58
  },
  boon_illumination: {
    open_handed: 84,
    detectives_hunch: 78,
    clairvoyance: 72,
    counterforce: 66,
    small_game: 62
  },
  baby_sitter: {
    borrowed_time: 88,
    well_make_it: 82,
    reassurance: 76,
    kindred: 70,
    we_see_you: 64,
    off_the_record: 58
  },
  borrowed_time: {
    baby_sitter: 86,
    off_the_record: 78,
    well_make_it: 72,
    reassurance: 66,
    kindred: 62
  },
  camaraderie: {
    reassurance: 88,
    borrowed_time: 82,
    kindred: 76,
    deliverance: 64,
    off_the_record: 58
  },
  change_of_plan: {
    built_to_last: 84,
    streetwise: 78,
    scavenger: 72,
    dramaturgy: 64,
    ace_in_the_hole: 58
  },
  come_and_get_me: {
    baby_sitter: 88,
    borrowed_time: 82,
    off_the_record: 76,
    mettle_of_man: 68,
    breakout: 62
  },
  counterforce: {
    inner_strength: 88,
    overzealous: 82,
    small_game: 78,
    detectives_hunch: 72,
    clairvoyance: 64
  },
  cut_loose: {
    lithe: 86,
    dance_with_me: 80,
    quick_and_quiet: 76,
    finesse: 70,
    windows_of_opportunity: 64
  },
  dark_theory: {
    circle_of_healing: 82,
    shadow_step: 78,
    exponential: 72,
    boon_illumination: 66,
    made_for_this: 58
  },
  five_moves_ahead: {
    windows_of_opportunity: 90,
    lithe: 84,
    finesse: 78,
    troubleshooter: 72,
    resilience: 66
  },
  flow_state: {
    circle_of_healing: 92,
    boon_illumination: 88,
    shadow_step: 84,
    exponential: 80,
    overzealous: 78,
    detectives_hunch: 76,
    botany_knowledge: 74,
    well_make_it: 72,
    desperate_measures: 70,
    leader: 68,
    counterforce: 64,
    small_game: 62
  },
  hardened: {
    inner_strength: 82,
    overzealous: 76,
    counterforce: 70,
    small_game: 66,
    detectives_hunch: 58
  },
  inner_focus: {
    empathy: 84,
    bond: 78,
    open_handed: 72,
    alert: 66,
    we_see_you: 60
  },
  last_stand: {
    decisive_strike: 84,
    off_the_record: 78,
    dead_hard: 72,
    resilience: 66,
    soul_guard: 58
  },
  leader: {
    prove_thyself: 86,
    better_together: 78,
    bond: 72,
    well_make_it: 66,
    vigil: 58
  },
  moment_of_glory: {
    plunderers_instinct: 84,
    appraisal: 78,
    ace_in_the_hole: 72,
    pharmacy: 66,
    streetwise: 58
  },
  one_two_three_four: {
    prove_thyself: 84,
    better_together: 78,
    friendly_competition: 72,
    deja_vu: 66,
    leader: 60
  },
  overcome: {
    lucky_break: 90,
    iron_will: 78,
    bite_the_bullet: 70,
    vigil: 66,
    blood_rush: 58
  },
  parental_guidance: {
    smash_hit: 90,
    head_on: 82,
    quick_and_quiet: 76,
    dance_with_me: 68,
    chemical_trap: 62
  },
  plunderers_instinct: {
    ace_in_the_hole: 92,
    appraisal: 86,
    pharmacy: 76,
    streetwise: 64,
    built_to_last: 58
  },
  premonition: {
    spine_chill: 96,
    light_footed: 88,
    urban_evasion: 82,
    calm_spirit: 76,
    alert: 70,
    dark_sense: 64,
    object_of_obsession: 58,
    windows_of_opportunity: 45
  },
  slippery_meat: {
    up_the_ante: 96,
    wicked: 82,
    deliverance: 72,
    kindred: 66,
    reassurance: 58,
    camaraderie: 52
  },
  rookie_spirit: {
    deja_vu: 86,
    prove_thyself: 78,
    better_together: 72,
    wiretap: 66,
    fogwise: 60
  },
  self_care: {
    botany_knowledge: 90,
    desperate_measures: 76,
    bite_the_bullet: 72,
    empathy: 64,
    solidarity: 58
  },
  small_game: {
    counterforce: 88,
    inner_strength: 82,
    overzealous: 76,
    detectives_hunch: 70,
    clairvoyance: 64
  },
  teamwork_full_circuit: {
    prove_thyself: 84,
    better_together: 78,
    friendly_competition: 72,
    corrective_action: 66,
    leader: 60
  },
  urban_evasion: {
    fixated: 84,
    lightweight: 78,
    distortion: 72,
    calm_spirit: 66,
    lucky_break: 58
  },
  were_gonna_live_forever: {
    buckle_up: 88,
    for_the_people: 82,
    soul_guard: 78,
    unbreakable: 74,
    exponential: 68,
    tenacity: 62,
    empathy: 58
  }
}

const CURATED_CENTRAL_WANTS = {
  balanced_landing: {
    windows_of_opportunity: 100,
    five_moves_ahead: 88,
    troubleshooter: 78,
    fixated: 65,
    resilience: 58,
    vigil: 45
  },
  dance_with_me: {
    lithe: 100,
    quick_and_quiet: 95,
    lucky_break: 90,
    lightweight: 82,
    iron_will: 76
  },
  ghost_notes: {
    lightweight: 95,
    self_preservation: 90,
    lucky_break: 88,
    iron_will: 82,
    quick_and_quiet: 74,
    sprint_burst: 40
  },
  lithe: {
    finesse: 100,
    windows_of_opportunity: 92,
    five_moves_ahead: 86,
    resilience: 82,
    troubleshooter: 78,
    cut_loose: 74,
    dance_with_me: 42
  },
  up_the_ante: {
    slippery_meat: 100,
    kindred: 62,
    resurgence: 55,
    off_the_record: 50,
    wicked: 30,
    deliverance: 28
  },
  rapid_response: {
    head_on: 92,
    vigil: 78,
    windows_of_opportunity: 68,
    sprint_burst: 58,
    five_moves_ahead: 52
  },
  vigil: {
    sprint_burst: 96,
    blood_rush: 72,
    ghost_notes: 62,
    fixated: 58,
    windows_of_opportunity: 52
  },
  extrasensory_perception: {
    open_handed: 100,
    urban_evasion: 94,
    sprint_burst: 90,
    lithe: 80,
    resilience: 76,
    windows_of_opportunity: 70,
    shadow_step: 62,
    flow_state: 58,
    circle_of_healing: 54
  }
}

const RECENT_CENTRAL_WANTS = {
  sprint_burst: {
    fixated: 100,
    vigil: 98,
    windows_of_opportunity: 94,
    ghost_notes: 84,
    blood_rush: 78,
    deja_vu: 70,
    resilience: 68
  },
  eyes_of_belmont: {
    fogwise: 100,
    scene_partner: 96,
    alert: 94,
    dark_sense: 92,
    inner_focus: 92,
    troubleshooter: 90,
    hardened: 90,
    object_of_obsession: 88,
    poised: 88,
    we_see_you: 88,
    strength_in_shadows: 86,
    baby_sitter: 86,
    wicked: 84,
    breakdown: 84,
    treacherous_crows: 84,
    rapid_response: 82,
    streetwise: 78
  },
  wide_open_throttle: {
    windows_of_opportunity: 96,
    finesse: 86,
    lithe: 80,
    smash_hit: 76,
    troubleshooter: 72,
    parental_guidance: 68,
    dance_with_me: 82,
    quick_and_quiet: 80,
    resilience: 74,
    cut_loose: 70,
    light_footed: 68
  },
  lend_a_hand: {
    detectives_hunch: 92,
    small_game: 86,
    botany_knowledge: 84,
    empathy: 80,
    well_make_it: 78,
    circle_of_healing: 74,
    counterforce: 68,
    inner_strength: 90,
    overzealous: 88,
    clairvoyance: 82,
    bond: 78,
    aftercare: 76,
    empathic_connection: 74,
    desperate_measures: 72,
    better_than_new: 70,
    solidarity: 68
  },
  cross_examination: {
    windows_of_opportunity: 88,
    lithe: 84,
    alert: 80,
    finesse: 76,
    lightweight: 72,
    quick_and_quiet: 68,
    fixated: 64,
    stake_out: 92,
    diversion: 88,
    calm_spirit: 78,
    urban_evasion: 74,
    premonition: 70,
    spine_chill: 68,
    hyperfocus: 66
  }
}

export const survivorCentralWants = Object.assign({}, MANUAL_CENTRAL_WANTS, CURATED_CENTRAL_WANTS, RECENT_CENTRAL_WANTS)

export const roleRecipes = {
  pallet_vault_chase: {
    slotNeeds: [
      { name: 'route_reading', roles: ['pallet_window_routing', 'resource_info', 'map_routing'], weight: 90 },
      { name: 'chase_conversion', roles: ['chase_reposition', 'exhaustion_spender', 'chase_extension'], weight: 84 },
      { name: 'pallet_value', roles: ['pallet_play', 'cooldown_support'], weight: 72 }
    ]
  },
  totem_heal_support: {
    slotNeeds: [
      { name: 'totem_access', roles: ['totem_interaction', 'resource_info'], weight: 90 },
      { name: 'healing_value', roles: ['heal_reset', 'heal_speed'], weight: 84 },
      { name: 'teammate_access', roles: ['teammate_locator', 'teamplay', 'stay_together'], weight: 72 }
    ]
  },
  light_mark_tracking: {
    slotNeeds: [
      { name: 'terror_radius_setup', roles: ['terror_radius_value', 'killer_path_tracking'], weight: 88 },
      { name: 'chase_conversion', roles: ['chase_reposition', 'pallet_window_routing', 'exhaustion_spender'], weight: 82 },
      { name: 'track_denial', roles: ['stealth', 'disappearance', 'scratch_mark_denial'], weight: 70 }
    ]
  },
  aura_extension: {
    slotNeeds: [
      { name: 'aura_source', roles: ['aura_information'], weight: 84 },
      { name: 'map_reading', roles: ['resource_info', 'map_routing'], weight: 76 },
      { name: 'macro_conversion', roles: ['generator_macro', 'hook_control'], weight: 64 }
    ]
  },
  sprint_burst_control: {
    slotNeeds: [
      { name: 'walking_control', roles: ['walking_reposition'], weight: 100 },
      { name: 'exhaustion_recovery', roles: ['cooldown_support', 'exhaustion_recovery'], weight: 98 },
      { name: 'route_safety', roles: ['pallet_window_routing', 'resource_info', 'map_routing'], weight: 94 }
    ]
  },
  timed_killer_aura_extension: {
    slotNeeds: [
      { name: 'timed_killer_aura', roles: ['timed_killer_aura'], weight: 100 },
      { name: 'trigger_support', roles: ['token_support', 'skillcheck_value'], weight: 92 },
      { name: 'generator_conversion', roles: ['repair_progress_or_skillcheck', 'generator_macro'], weight: 86 }
    ]
  },
  specialist_chest_skillcheck: {
    slotNeeds: [
      { name: 'chest_tokens', roles: ['chest_loot', 'chest_finding', 'item_conversion'], weight: 98 },
      { name: 'great_check_access', roles: ['skillcheck_value', 'token_support'], weight: 94 },
      { name: 'generator_conversion', roles: ['repair_progress_or_skillcheck', 'generator_macro'], weight: 88 }
    ]
  },
  basement_self_unhook: {
    slotNeeds: [
      { name: 'self_unhook', roles: ['self_unhook_attempts'], weight: 88 },
      { name: 'survival', roles: ['post_unhook_survival', 'anti_tunnel'], weight: 78 },
      { name: 'hook_reading', roles: ['hook_control'], weight: 64 }
    ]
  },
  brought_item_economy: {
    slotNeeds: [
      { name: 'charge_efficiency', roles: ['charge_efficiency'], weight: 86 },
      { name: 'item_recovery', roles: ['item_recovery'], weight: 80 },
      { name: 'item_conversion', roles: ['toolbox_or_medkit_value'], weight: 72 }
    ]
  },
  chest_loot: {
    slotNeeds: [
      { name: 'chest_finding', roles: ['chest_finding'], weight: 86 },
      { name: 'item_quality', roles: ['item_quality'], weight: 80 },
      { name: 'item_conversion', roles: ['item_conversion'], weight: 72 }
    ]
  },
  duo_proximity: {
    slotNeeds: [
      { name: 'stay_together', roles: ['stay_together'], weight: 86 },
      { name: 'link_payoff', roles: ['shared_haste', 'post_heal_link'], weight: 78 },
      { name: 'locator', roles: ['teammate_locator'], weight: 66 }
    ]
  },
  exhaustion_vault_chase: {
    slotNeeds: [
      { name: 'vault_value', roles: ['vault_speed', 'pallet_window_routing'], weight: 88 },
      { name: 'chase_pathing', roles: ['resource_info', 'map_routing'], weight: 82 },
      { name: 'chase_conversion', roles: ['chase_extension', 'anti_loop_info'], weight: 76 }
    ]
  },
  heal_noise_denial: {
    slotNeeds: [
      { name: 'heal_value', roles: ['heal_reset'], weight: 86 },
      { name: 'noise_cut', roles: ['noise_denial'], weight: 82 },
      { name: 'escape', roles: ['line_of_sight_break', 'scratch_mark_denial'], weight: 68 }
    ]
  },
  hook_luck_gamble: {
    slotNeeds: [
      { name: 'luck_engine', roles: ['luck'], weight: 92 },
      { name: 'self_unhook_attempts', roles: ['self_unhook_attempts'], weight: 90 },
      { name: 'post_unhook_survival', roles: ['post_unhook_survival', 'hook_control'], weight: 72 }
    ]
  },
  linked_survivor: {
    slotNeeds: [
      { name: 'stay_together', roles: ['stay_together'], weight: 86 },
      { name: 'link_payoff', roles: ['shared_haste', 'post_heal_link'], weight: 80 },
      { name: 'locator', roles: ['teammate_locator'], weight: 64 }
    ]
  },
  locker_exhaustion_info: {
    slotNeeds: [
      { name: 'locker_trigger', roles: ['locker_trigger'], weight: 88 },
      { name: 'exhaustion_reading', roles: ['exhaustion_info'], weight: 82 },
      { name: 'reposition', roles: ['chase_reposition', 'resource_info'], weight: 70 }
    ]
  },
  post_unhook_survival: {
    slotNeeds: [
      { name: 'anti_tunnel', roles: ['anti_tunnel'], weight: 86 },
      { name: 'reset', roles: ['heal_reset'], weight: 76 },
      { name: 'reposition', roles: ['chase_reposition', 'noise_denial'], weight: 68 }
    ]
  },
  post_vault_disappearance: {
    slotNeeds: [
      { name: 'vault_trigger', roles: ['exhaustion_vault_chase', 'vault_trigger'], weight: 90 },
      { name: 'noise_cut', roles: ['noise_denial'], weight: 84 },
      { name: 'track_denial', roles: ['scratch_mark_denial', 'line_of_sight_break'], weight: 80 }
    ]
  },
  reactive_heal_link: {
    slotNeeds: [
      { name: 'heal_value', roles: ['heal_reset'], weight: 86 },
      { name: 'proximity', roles: ['stay_together', 'post_heal_link'], weight: 78 },
      { name: 'locator', roles: ['teammate_locator'], weight: 66 }
    ]
  },
  scratch_mark_disappearance: {
    slotNeeds: [
      { name: 'track_denial', roles: ['scratch_mark_denial'], weight: 90 },
      { name: 'line_of_sight', roles: ['line_of_sight_break'], weight: 82 },
      { name: 'noise_cut', roles: ['noise_denial'], weight: 72 }
    ]
  },
  status_exhaustion_battery: {
    slotNeeds: [
      { name: 'exhaustion_spender', roles: ['exhaustion_spender'], weight: 90 },
      { name: 'cooldown_value', roles: ['cooldown_support', 'exhaustion_info'], weight: 80 },
      { name: 'reposition', roles: ['chase_reposition'], weight: 68 }
    ]
  },
  stealth_recon_conversion: {
    slotNeeds: [
      { name: 'activation', roles: ['crouch_mobility'], weight: 88 },
      { name: 'objective_conversion', roles: ['objective_routing'], weight: 84 },
      { name: 'safety', roles: ['exhaustion_spender', 'chase_reposition', 'pallet_window_routing', 'scratch_mark_denial'], weight: 76 }
    ]
  },
  crouch_omniscience: {
    slotNeeds: [
      { name: 'aura_range', roles: ['aura_extension'], weight: 100 },
      { name: 'crouch_mobility', roles: ['crouch_mobility'], weight: 94 },
      { name: 'chase_safety', roles: ['exhaustion_spender', 'chase_reposition', 'pallet_window_routing'], weight: 82 }
    ]
  },
  boon_recon_setup: {
    slotNeeds: [
      { name: 'primary_boon', roles: ['boon_setup'], weight: 90 },
      { name: 'second_boon', roles: ['boon_setup'], weight: 86 },
      { name: 'safety', roles: ['exhaustion_spender', 'chase_reposition', 'pallet_window_routing'], weight: 74 }
    ]
  },
  totem_consume_aura: {
    slotNeeds: [
      { name: 'totem_access', roles: ['totem_interaction'], weight: 86 },
      { name: 'map_reading', roles: ['resource_info', 'map_routing'], weight: 80 },
      { name: 'aura_value', roles: ['aura_information'], weight: 68 }
    ]
  },
  vertical_exhaustion_chase: {
    slotNeeds: [
      { name: 'route_reading', roles: ['resource_info', 'map_routing'], weight: 88 },
      { name: 'reposition', roles: ['chase_reposition'], weight: 80 },
      { name: 'cooldown_support', roles: ['cooldown_support'], weight: 58 }
    ]
  }
}

export const functionalRedundancyGroups = {
  self_unhook_solution: ['slippery_meat', 'deliverance', 'wicked'],
  post_vault_disappearance: ['dance_with_me', 'quick_and_quiet', 'cut_loose'],
  scratch_mark_disappearance: ['lucky_break', 'lightweight', 'ghost_notes', 'self_preservation'],
  exhaustion_spender: ['lithe', 'sprint_burst', 'balanced_landing', 'dead_hard', 'head_on', 'overcome', 'smash_hit', 'dramaturgy', 'background_player'],
  hook_control: ['kindred', 'reassurance', 'camaraderie'],
  pickup_denial: ['background_player', 'flashbang', 'saboteur', 'breakout'],
  teammate_locator: ['bond', 'aftercare', 'empathy'],
  passive_recon: ['extrasensory_perception', 'still_sight', 'clairvoyance', 'visionary', 'premonition', 'dark_sense', 'alert', 'inner_focus', 'object_of_obsession', 'spine_chill'],
  passive_stealth: ['calm_spirit', 'distortion', 'lightweight', 'urban_evasion', 'self_preservation', 'poised', 'low_profile']
}

export const functionalRedundancyPolicy = {
  self_unhook_solution: { freeCount: 1, penaltyPerExtra: 24 },
  post_vault_disappearance: { freeCount: 2, penaltyPerExtra: 14 },
  scratch_mark_disappearance: { freeCount: 2, penaltyPerExtra: 14 },
  exhaustion_spender: { freeCount: 1, penaltyPerExtra: 35 },
  hook_control: { freeCount: 2, penaltyPerExtra: 14 },
  pickup_denial: { freeCount: 2, penaltyPerExtra: 16 },
  teammate_locator: { freeCount: 1, penaltyPerExtra: 18 },
  passive_recon: { freeCount: 1, penaltyPerExtra: 28 },
  passive_stealth: { freeCount: 1, penaltyPerExtra: 24 }
}

export function getSurvivorCentralWantWeight (centralPerkId, candidatePerkId) {
  const centralWants = survivorCentralWants[getSurvivorGameplayPerkId(centralPerkId)]
  if (!centralWants) return null
  const candidateGameplayId = getSurvivorGameplayPerkId(candidatePerkId)
  return typeof centralWants[candidateGameplayId] === 'number' ? centralWants[candidateGameplayId] : null
}

export function hasSurvivorCentralWants (centralPerkId) {
  return Boolean(survivorCentralWants[getSurvivorGameplayPerkId(centralPerkId)])
}

export function hasExplicitSurvivorCentralProfile (centralPerkId) {
  return Boolean(EXPLICIT_CENTRAL_PROFILE_OVERRIDES[getSurvivorGameplayPerkId(centralPerkId)])
}

export function getSurvivorPerkRoleIds (perkId) {
  const role = survivorPerkRoles[getSurvivorGameplayPerkId(perkId)]
  if (!role) return []
  return Array.from(new Set(
    [role.primaryRole]
      .concat(role.secondaryRoles, role.optionalRoles)
      .concat(getSurvivorInteractionRoleIds(perkId))
  ))
}

export function getSurvivorCentralProfile (perkId) {
  return survivorCentralProfiles[getSurvivorGameplayPerkId(perkId)] || null
}

const RECIPE_ROLE_ALIASES = {
  action_speed: ['action_speed', 'generator_macro', 'heal_reset', 'teamplay'],
  cooldown_support: ['cooldown_support', 'exhaustion_recovery', 'teamplay'],
  exhaustion_recovery: ['exhaustion_recovery', 'cooldown_support'],
  exhaustion_spender: ['exhaustion_spender', 'chase_reposition', 'chase'],
  gate_info: ['gate_info', 'endgame', 'aura_information'],
  gen_bait: ['gen_bait', 'generator_macro', 'misdirection'],
  gen_protection: ['gen_protection', 'generator_macro'],
  heal_speed: ['heal_speed', 'heal_reset'],
  injured_value: ['injured_value', 'chase', 'anti_slug'],
  item_recovery: ['item_recovery', 'item_economy'],
  line_of_sight_break: ['line_of_sight_break', 'disappearance', 'stealth'],
  long_heal_window: ['long_heal_window', 'heal_reset'],
  map_routing: ['map_routing', 'resource_info', 'aura_information'],
  misdirection: ['misdirection', 'stealth', 'disappearance'],
  noise_denial: ['noise_denial', 'stealth', 'disappearance'],
  objective_interaction: ['objective_interaction', 'generator_macro', 'totem_interaction', 'item_economy'],
  pallet_window_routing: ['pallet_window_routing', 'resource_info', 'aura_information', 'pallet_play'],
  protection_hit: ['protection_hit', 'save', 'anti_tunnel'],
  recovery_speed: ['recovery_speed', 'anti_slug'],
  repair_progress_or_skillcheck: ['repair_progress_or_skillcheck', 'skillcheck_value', 'generator_macro'],
  repair_speed: ['repair_speed', 'generator_macro'],
  resource_info: ['resource_info', 'aura_information'],
  safe_reset: ['safe_reset', 'heal_reset', 'line_of_sight_break'],
  scratch_mark_denial: ['scratch_mark_denial', 'disappearance', 'stealth'],
  support_action: ['support_action', 'teamplay', 'heal_reset', 'save'],
  token_support: ['token_support', 'skillcheck_value']
}

function expandRecipeRoles (role) {
  return RECIPE_ROLE_ALIASES[role] || [role]
}

function createFallbackRoleRecipe (profile) {
  if (!profile) return null
  const roleSlots = []
  profile.requiredRoles.forEach((role, index) => {
    roleSlots.push({
      name: `required_${index + 1}`,
      roles: expandRecipeRoles(role),
      weight: 48 - index * 4
    })
  })
  profile.preferredRoles.forEach((role, index) => {
    roleSlots.push({
      name: `preferred_${index + 1}`,
      roles: expandRecipeRoles(role),
      weight: 38 - index * 4
    })
  })
  return roleSlots.length
    ? {
      generated: true,
      slotNeeds: roleSlots.slice(0, 3)
    }
    : null
}

export function getSurvivorRoleRecipe (centralPerkId) {
  const profile = getSurvivorCentralProfile(centralPerkId)
  return profile ? roleRecipes[profile.archetype] || createFallbackRoleRecipe(profile) : null
}

export function getSurvivorCentralProfileAudit (centralPerkId) {
  const profile = getSurvivorCentralProfile(centralPerkId)
  const recipe = getSurvivorRoleRecipe(centralPerkId)
  const explicitRecipe = Boolean(profile && roleRecipes[profile.archetype])
  const explicitProfile = hasExplicitSurvivorCentralProfile(centralPerkId)
  const explicitCentralWants = hasSurvivorCentralWants(centralPerkId)
  const hasRecipe = Boolean(recipe)
  let qualityLabel = 'Default'

  if (explicitProfile && explicitCentralWants) {
    qualityLabel = 'Curated'
  } else if (explicitProfile) {
    qualityLabel = 'Profiled'
  } else if (hasRecipe) {
    qualityLabel = 'Recipe Default'
  }

  return {
    perkId: centralPerkId,
    archetype: profile ? profile.archetype : null,
    explicitProfile: explicitProfile,
    explicitCentralWants: explicitCentralWants,
    explicitRecipe: explicitRecipe,
    hasRecipe: hasRecipe,
    qualityLabel: qualityLabel,
    requiredRoles: profile ? profile.requiredRoles.slice() : [],
    preferredRoles: profile ? profile.preferredRoles.slice() : [],
    forbiddenRoles: profile ? profile.forbiddenRoles.slice() : [],
    recipeSlots: recipe ? recipe.slotNeeds.map(slotNeed => slotNeed.name) : []
  }
}

export function getSurvivorRoleRecipeMatches (centralPerkId, candidatePerkId) {
  const centralGameplayId = getSurvivorGameplayPerkId(centralPerkId)
  const recipe = getSurvivorRoleRecipe(centralGameplayId)
  const candidateRoles = getSurvivorPerkRoleIds(getSurvivorGameplayPerkId(candidatePerkId))
  if (!recipe) return []
  return recipe.slotNeeds.reduce((matches, slotNeed) => {
    const candidateRole = slotNeed.roles.find(role => candidateRoles.indexOf(role) !== -1)
    if (candidateRole) {
      matches.push({
        candidateRole: candidateRole,
        centralRole: survivorCentralProfiles[centralGameplayId].archetype,
        generated: Boolean(recipe.generated),
        slotName: slotNeed.name,
        weight: slotNeed.weight
      })
    }
    return matches
  }, [])
}

export function getSurvivorRoleRecipeMatch (centralPerkId, candidatePerkId) {
  const matches = getSurvivorRoleRecipeMatches(centralPerkId, candidatePerkId)
    .sort((first, second) => second.weight - first.weight)
  if (!matches.length) return null
  return matches[0]
}

export default survivorPerkRoles
